import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Clock, MapPin, Play, Square, Coffee, TrendingUp, Calendar, 
  CheckCircle, AlertCircle, Timer
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const TimeTracking = () => {
  const { user } = useAuth();
  const [todayEntry, setTodayEntry] = useState(null);
  const [weekEntries, setWeekEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    fetchTimeData();
    getLocation();
  }, []);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log("Location not available:", error);
        }
      );
    }
  };

  const fetchTimeData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/time-entries?start_date=${weekAgo}&end_date=${today}`, {
        headers, withCredentials: true
      });
      
      const entries = response.data;
      setWeekEntries(entries);
      
      const todayData = entries.find(e => e.date === today);
      setTodayEntry(todayData);
    } catch (error) {
      console.error("Error fetching time data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/time-entries/clock-in`, {
        location,
        work_type: "office"
      }, { headers, withCredentials: true });
      
      toast.success(`Entrada registrada: ${response.data.time}`);
      if (response.data.status === "late") {
        toast.warning("Has llegado tarde");
      }
      fetchTimeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar entrada");
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/time-entries/clock-out`, {
        location
      }, { headers, withCredentials: true });
      
      toast.success(`Salida registrada: ${response.data.time}`);
      toast.info(`Total de horas: ${response.data.total_hours}h`);
      fetchTimeData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar salida");
    } finally {
      setClockingOut(false);
    }
  };

  const totalWeekHours = weekEntries.reduce((acc, e) => acc + (e.total_hours || 0), 0);
  const totalOvertimeHours = weekEntries.reduce((acc, e) => acc + (e.overtime_hours || 0), 0);
  const daysWorked = weekEntries.filter(e => e.total_hours > 0).length;

  const isClockedIn = todayEntry && todayEntry.check_in && !todayEntry.check_out;
  const isClockedOut = todayEntry && todayEntry.check_out;

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout 
      title="Control Horario" 
      subtitle="Registra tu entrada y salida con geolocalización"
    >
      {/* Clock In/Out Card */}
      <Card className="mb-6 border-slate-200 bg-gradient-to-br from-cyan-50 to-white">
        <CardContent className="p-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Time Display */}
            <div className="text-center lg:text-left">
              <p className="text-6xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                {getCurrentTime()}
              </p>
              <p className="text-slate-500">
                {new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              {location && (
                <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                  <MapPin className="w-4 h-4" />
                  <span>Ubicación detectada</span>
                </div>
              )}
            </div>

            {/* Clock Buttons */}
            <div className="flex flex-col items-center gap-4">
              {todayEntry && (
                <div className="text-center mb-2">
                  <p className="text-sm text-slate-500">Estado de hoy</p>
                  <div className="flex items-center gap-4 mt-1">
                    {todayEntry.check_in && (
                      <Badge className="badge-success">
                        <Play className="w-3 h-3 mr-1" />
                        Entrada: {todayEntry.check_in}
                      </Badge>
                    )}
                    {todayEntry.check_out && (
                      <Badge className="badge-info">
                        <Square className="w-3 h-3 mr-1" />
                        Salida: {todayEntry.check_out}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex gap-4">
                <Button
                  size="lg"
                  className={`h-16 px-8 text-lg ${
                    isClockedIn 
                      ? 'bg-slate-300 cursor-not-allowed' 
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                  onClick={handleClockIn}
                  disabled={isClockedIn || isClockedOut || clockingIn}
                  data-testid="clock-in-btn"
                >
                  {clockingIn ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Play className="w-6 h-6 mr-2" />
                      Entrada
                    </>
                  )}
                </Button>
                
                <Button
                  size="lg"
                  className={`h-16 px-8 text-lg ${
                    !isClockedIn 
                      ? 'bg-slate-300 cursor-not-allowed' 
                      : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                  onClick={handleClockOut}
                  disabled={!isClockedIn || clockingOut}
                  data-testid="clock-out-btn"
                >
                  {clockingOut ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Square className="w-6 h-6 mr-2" />
                      Salida
                    </>
                  )}
                </Button>
              </div>

              {isClockedOut && (
                <Badge className="badge-success text-base py-2 px-4">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Jornada completada: {todayEntry.total_hours}h
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Timer className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalWeekHours.toFixed(1)}h</p>
                <p className="text-sm text-slate-500">Horas esta semana</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalOvertimeHours.toFixed(1)}h</p>
                <p className="text-sm text-slate-500">Horas extra</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{daysWorked}</p>
                <p className="text-sm text-slate-500">Días trabajados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {daysWorked > 0 ? (totalWeekHours / daysWorked).toFixed(1) : 0}h
                </p>
                <p className="text-sm text-slate-500">Promedio diario</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly History */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-600" />
            Historial de la Semana
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : weekEntries.length === 0 ? (
            <div className="text-center text-slate-500 py-8">
              <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No hay registros esta semana</p>
            </div>
          ) : (
            <div className="space-y-3">
              {weekEntries.slice().reverse().map((entry) => (
                <div 
                  key={entry.entry_id}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border border-slate-100"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-900">
                        {new Date(entry.date).toLocaleDateString('es-MX', { day: '2-digit' })}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(entry.date).toLocaleDateString('es-MX', { weekday: 'short' })}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <Badge className={
                          entry.status === "present" ? "badge-success" :
                          entry.status === "late" ? "badge-warning" : "badge-info"
                        }>
                          {entry.status === "present" ? "Presente" :
                           entry.status === "late" ? "Tarde" : entry.status}
                        </Badge>
                        <span className="text-sm text-slate-600">
                          {entry.check_in} - {entry.check_out || "En curso"}
                        </span>
                      </div>
                      {entry.check_in_location && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                          <MapPin className="w-3 h-3" />
                          Con ubicación
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900">
                      {entry.total_hours ? `${entry.total_hours}h` : '-'}
                    </p>
                    {entry.overtime_hours > 0 && (
                      <p className="text-xs text-amber-600">+{entry.overtime_hours}h extra</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};

export default TimeTracking;
